/**
 * Single source of truth for anything that depends on where the site is deployed.
 *
 * CHANGE `origin` TO YOUR DOMAIN AND EVERYTHING ELSE FOLLOWS:
 * canonical tags, Open Graph URLs, JSON-LD, sitemap.xml and robots.txt.
 *
 * Serving from a sub-path rather than a domain root? Set `basePath` to
 * '/<sub-path>/' and `origin` to the host it is served from.
 */
export const SITE = {
  /** No trailing slash. */
  origin: 'https://atsresumekit.com',
  /** Leading and trailing slash. '/' for a custom domain at the root. */
  basePath: '/',
  /**
   * The brand, written as one word everywhere it is displayed or declared:
   * titles, logo text, og:site_name, schema `name`, llms.txt (owner decision,
   * 2026-10-22). The spaced form survives only as `alternateName`.
   */
  name: 'ATSResumeKit',
  /** The spaced spelling, emitted only as the Organization's `alternateName`. */
  alternateName: 'ATS Resume Kit',
  /**
   * The descriptive phrase, not the brand — it is what someone types into a
   * search box, and it drives the home page title and H1.
   *
   * Kept deliberately distinct from `name`. "ATS resume checker" is a real query
   * with real volume; "ATSResumeKit" is a brand nobody has searched for yet.
   * A title built only from the brand would rank for nothing on day one.
   */
  tagline: 'Free ATS Resume Checker & Builder',
  description:
    'Free ATS resume checker and builder. Check your score, match any job description and fix weak bullets — 100% in your browser. No sign-up.',
  locale: 'en_US',
  lang: 'en',
  /*
   * There was a `twitter: '@atsresumedev'` handle here. Removed rather than
   * rebranded: nothing read it. The prerenderer and `useSeo` both emit
   * `twitter:card`, `title`, `description` and `image`, and neither has ever
   * emitted `twitter:site` — so the field was decoration that looked like
   * configuration, and rebranding it would have preserved that illusion. Add it
   * back alongside a real `twitter:site` tag if an account is ever created.
   */
  /*
   * There is no `repo` field. The product does not link to a source repository
   * anywhere, so a URL here would configure nothing — and a config key that
   * nothing reads is how the dead `twitter` handle survived three rebrands.
   * Add it back together with the links, if links are ever wanted.
   */
  /*
   * There was a `license: 'MIT'` here. Nothing read it once the licence claim
   * was removed from the `SoftwareApplication` markup, and the product no longer
   * advertises its licensing to users at all. The LICENSE file stays — the code
   * is still licensed, it is simply not part of how this is positioned.
   */
  /**
   * Leave empty to render no AdSense markup at all.
   *
   * Deliberately unset at launch. Monetisation is a decision with a date, not a
   * default: shipping ads before there is traffic costs trust and earns nothing.
   * Revisit once organic sessions pass roughly 10K/month.
   */
  adsenseClientId: 'ca-pub-2729930026460873',
  /**
   * Cookieless analytics endpoint (Plausible / Umami / GoatCounter). Empty means
   * zero network requests and zero script tags — a fork stays request-free.
   * Never receives resume content, and never fires on /resume-builder. See
   * lib/analytics.ts.
   */
  analyticsEndpoint: '',
  /**
   * Google Analytics 4 measurement id. Empty disables it completely.
   *
   * Unlike `analyticsEndpoint` above, this is **not** cookieless: GA4 stores an
   * identifier and sends it to Google. Two consequences, handled in
   * `scripts/gaHead.ts` / `lib/ga.ts` rather than left to whoever reads this
   * next:
   *
   *   1. It loads unconditionally, on every route including `/resume-builder` —
   *      no consent gate, no Do Not Track / Global Privacy Control exemption.
   *      See the Analytics section of `/privacy` for what that means.
   *   2. Setting it widens the Content-Security-Policy, which the build derives.
   *      Leaving it blank keeps the policy tight.
   *
   * Setting this also changes what `/privacy` says. That page is generated from
   * configuration precisely so the claim and the behaviour cannot diverge.
   */
  gaMeasurementId: 'G-B4QYDFLP1R',
  /**
   * Bing Webmaster Tools verification code (the content of its
   * `msvalidate.01` meta tag). Empty renders no tag. Not needed if the site
   * is verified in Bing by importing it from Google Search Console.
   */
  // TODO(owner): Bing verification code, if you verify with the meta tag method.
  bingSiteVerification: '',
  /**
   * Publisher identity, for search and AI-retrieval systems.
   *
   * E-E-A-T in 2026 is assessed on *entities*, not pages. A retrieval system
   * deciding whether to cite a page asks "who publishes this, and why should
   * this claim be trusted?" — and career advice sits in the "Your Money or Your
   * Life" category, where that question is weighted hardest. With no author and
   * no organisation node, there is no answer, and good content goes uncited.
   *
   * `founded` and `logo` are facts and are filled in. **`author` is deliberately
   * empty**: a `Person` entity with an invented name or invented credentials is
   * fabrication, and it is the one thing this product has consistently refused to
   * do — the same reason the resume tools will not invent your metrics. Fill it
   * with a real person, or leave it blank and ship the Organization alone.
   *
   * When `author.name` is empty, no `Person` markup is emitted at all. Nothing
   * false is published either way.
   */
  founded: '2026',
  /**
   * The publisher entity: the name used for `Organization`, `publisher` and
   * `author` (when no person is configured) in structured data, and in the
   * visible "By …" byline on guides and on /about. Confirmed by the owner on
   * 2026-10-18. `name` above stays the product's display name.
   *
   * TODO(owner): the maintainer's personal name has not been provided. It
   * belongs in `author.name` below, never invented here.
   */
  publisher: {
    name: 'ATSResumeKit',
    url: 'https://atsresumekit.com',
    /**
     * The publisher's own profiles, emitted as Organization `sameAs`. Only
     * real, resolvable URLs belong here; an empty value is left out.
     */
    profiles: {
      // TODO(owner): LinkedIn page URL, e.g. https://www.linkedin.com/company/...
      linkedin: '',
      // TODO(owner): Pinterest profile URL.
      pinterest: '',
      // TODO(owner): Product Hunt product or maker URL.
      productHunt: '',
    },
  },
  /**
   * The byline author on guides, /methodology and /evidence: the team, typed
   * as an Organization (owner decision, 2026-10-22). A team is not a person,
   * so it is never emitted as `Person`; a real person goes in `author` below
   * and then takes precedence in Article markup.
   */
  team: {
    name: 'ATSResumeKit Team',
    path: '/author/atsresumekit-team',
    // TODO(owner): confirm this bio wording.
    bio: 'The ATSResumeKit Team builds and maintains the free resume tools on this site and writes its guides. Every scoring rule is published on the methodology page, and the parsing claims are tested and reported on the evidence page.',
    // TODO(owner): photo. A square image in public/, referenced here as '/team.jpg'. Nothing renders while it is empty.
    photo: '',
    // TODO(owner): LinkedIn URL. Only real, resolvable profile URLs belong here.
    sameAs: [] as string[],
  },
  /** Absolute path to a square logo, served from this origin. */
  logo: '/apple-touch-icon.svg',
  author: {
    /** Real full name. Leave empty rather than inventing one. */
    name: '',
    /** e.g. 'Founder and maintainer'. */
    jobTitle: '',
    /** One or two sentences. What makes this person worth trusting on resumes. */
    bio: '',
    /**
     * Profiles that corroborate the person elsewhere — LinkedIn, GitHub, a
     * personal site. `sameAs` is what lets a knowledge graph connect the entity
     * to an existing identity instead of treating it as an unknown string.
     */
    sameAs: [] as string[],
  },
  /**
   * Contact form, delivered by Web3Forms.
   *
   * Web3Forms accepts a POST from a static page and emails it on, which is how a
   * site with no backend can still have a working form. The access key is a
   * *public* key by design — it identifies the destination mailbox, not the
   * sender — so shipping it in the bundle is how the service is meant to be used
   * and is not a leaked secret.
   *
   * **This is the product's first outbound third-party request, and it changes
   * two things:**
   *
   *   1. `connect-src` must allow `api.web3forms.com`, or the browser blocks the
   *      submission. `scripts/generate-csp.ts` adds it only when a key is set.
   *   2. `/privacy` says different words when a key is set, because "no
   *      third-party requests" stops being unconditionally true.
   *
   * The important nuance, and the reason the claim survives: **nothing is sent
   * on page load.** The request happens only when a person fills in the form and
   * presses Send. A visitor who never uses the form still generates zero
   * third-party traffic, and the privacy page says exactly that.
   *
   * Blank disables the form and shows the alternative channels instead.
   */
  web3formsKey: 'decc2486-1489-454a-98bb-ac50d6aa2b41',
  /**
   * Contact and legal identity.
   *
   * Empty by design. The pages that use these render differently rather than
   * printing a placeholder — the same reasoning applied to `author`: a mailbox
   * nobody reads, or a legal entity that does not exist, costs more trust than
   * an honest absence.
   */
  contact: {
    /** Shown as a direct address alongside the form. Blank hides it. */
    email: '',
    /** Legal entity, if one exists. Blank means the terms speak for the project. */
    legalEntity: '',
    /** e.g. 'England and Wales'. Blank omits the governing-law clause entirely. */
    jurisdiction: '',
  },
  /** Last substantive change to the terms. Rendered on /terms. */
  termsUpdated: '2026-10-21',
  /** Last substantive change to the privacy policy. Rendered on /privacy. */
  privacyUpdated: '2026-10-21',
  /*
   * There was a `themeColor: '#0f172a'` here, stale from the indigo palette and
   * read by nothing. It could not have been correct anyway: `index.html` now
   * declares two `theme-color` tags, one per colour scheme, and a single string
   * cannot express that. The values live beside the tags they configure, and the
   * canonical source for both is `--color-canvas` in `src/index.css`.
   */
} as const

/**
 * Placeholder values that must be replaced before a public build.
 *
 * A previous build shipped this placeholder domain into 120 sitemap URLs, which
 * tells a search engine that the entire site duplicates a domain nobody owns.
 *
 * `scripts/check-config.ts` fails the production build while it is still set, so
 * that class of mistake cannot reach users again.
 */
export const PLACEHOLDERS = {
  origin: 'https://atsresume.dev',
} as const

export const configProblems = (): string[] => {
  const problems: string[] = []
  /*
   * Widened to `string` on purpose. `SITE` is a literal-typed object, so once a
   * real value is configured TypeScript proves the comparison can never be true
   * and rejects it as a mistake (TS2367) — which would mean the release gate
   * stops compiling the moment it starts passing, and a fork that pasted the
   * placeholders back in would get no check at all. The comparison has to stay a
   * runtime one, because that is when the value actually matters.
   */
  const origin: string = SITE.origin
  if (origin === PLACEHOLDERS.origin) {
    problems.push(
      `SITE.origin is still the placeholder (${PLACEHOLDERS.origin}). It is written into every canonical URL, Open Graph tag, JSON-LD block and all sitemap entries.`,
    )
  }
  if (!/^https:\/\//.test(origin)) problems.push('SITE.origin must be an absolute https URL.')
  if (origin.endsWith('/')) problems.push('SITE.origin must not have a trailing slash.')
  return problems
}

export const absoluteUrl = (path: string): string => {
  const clean = path.startsWith('/') ? path : `/${path}`
  return `${SITE.origin}${clean === '/' ? '/' : clean.replace(/\/$/, '')}`
}

/** A config date such as `termsUpdated`, as page text, e.g. "October 21, 2026". */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * "October 2026" from "2026-10-22", for visible "Updated" dates. Pure string
 * work rather than toLocaleDateString, so the prerendered HTML and the hydrated
 * page cannot format it differently.
 */
export const formatMonthYear = (iso: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(iso)
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined
  return match && month ? `${month} ${match[1]}` : iso
}

export const formatSiteDate = (iso: string): string => {
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
}
